import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import useCallbackRef from 'hooks/useCallbackRef';
import { Button, Loader, Modal } from 'ncoded-component-library';
import api from 'api';
import DownloadIcon from 'icons/Download.icon';
import { PdfViewer } from 'components/PdfViewer';
import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { useTranslation } from 'react-i18next';
import {
  extractNodeWatermarkSettings,
  getUserFullName,
  isDwg,
  isEml,
  isImage,
  isOfficeFileType,
  isPdf,
  isVideo,
} from 'utils';
import useCloseOnEscape from 'hooks/useCloseOnEscape';
import useFilePreviewTrackTime from 'hooks/useFilePreviewTrackTime';
import useDisableRightClick from 'hooks/useDisableRightClick';
import { SoftSignal } from 'models/Lead';
import SoftSignals from './components/SoftSignals';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { BUYING_PORTFOLIO_ROUTE } from 'data/global';
import { useNavigate } from 'react-router-dom';
import useMixpanelDocumentEvents from 'hooks/useMixpanelDocumentEvents';
import OfficeFilePreview from 'components/OfficeFilePreview';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import { DataRoomFileBufferPreview } from 'models/File';
import useStartDownloadingFile from 'hooks/useStartDownloadingFile';
import CannotPreviewInBrowser from 'components/CannotPreviewInBrowser';
import AutodeskViewer from 'components/AutodeskViewer';

import '@react-pdf-viewer/core/lib/styles/index.css';
import './DataroomNodeViewModal.styles.scss';
import './DataroomNodeViewModal.styles.responsive.scss';

export type SoftSignalsDetails = {
  displayBanner?: boolean;
  lastSoftSignal?: SoftSignal;
};

type DataroomNodeViewModalProps = {
  className?: string;
  node: DataRoomNode;
  isCurrentlyActiveNode?: boolean;
  dataRoomName?: string;
};

const DataroomNodeViewModal: ForwardRefRenderFunction<
  OverlayRef,
  DataroomNodeViewModalProps
> = (props, ref) => {
  const { className, node, dataRoomName, isCurrentlyActiveNode = true } = props;

  const {
    _id: nodeId,
    name: documentName,
    dataRoomId,
    canDownload,
    version,
    dataRoom,
  } = node || {};

  const { t } = useTranslation();

  const classes = classNames('data-room-node-view-modal', className);

  const [data, setData] = useState<DataRoomFileBufferPreview>();
  const [isUrlFetching, setIsUrlFetching] = useState(false);

  const {
    currentUser: { firstName, lastName, email },
  } = useContext(CurrentUserContext);
  const { popSuccess } = useContext(PopNotificationsContext);

  const navigate = useNavigate();

  const [modal, modalRef] = useCallbackRef<OverlayRef>();
  useImperativeHandle(ref, () => modal, [modal]);

  const isModalOpen = modal?.openState;

  const {
    url,
    type,
    autodeskToken,
    urn,
    isAutodeskProccessed,
    displayBanner,
    lastSoftSignal,
  } = data || {};

  const userFullName = useMemo(
    () => getUserFullName(firstName, lastName),
    [firstName, lastName],
  );

  useDisableRightClick(isModalOpen);

  const startDownloadingFile = useStartDownloadingFile();

  const RenderTitle = useMemo(
    () =>
      canDownload && (
        <Button
          variant="outline"
          className="data-room-node-view-modal__download"
          icon={DownloadIcon}
          onClick={() => startDownloadingFile(nodeId)}
        >
          {t('download')}
        </Button>
      ),
    [canDownload, nodeId, startDownloadingFile, t],
  );

  const fetchUrl = useCallback(async () => {
    try {
      setIsUrlFetching(true);

      const { data } = await api.dataRoom.nodes.getFileBufferPreview(
        nodeId,
        isCurrentlyActiveNode,
      );

      if (data) {
        const { type, isAutodeskProccessed, urn } = data;

        if (isEml(type) || (isDwg(type) && (!urn || !isAutodeskProccessed)))
          startDownloadingFile(nodeId);

        setData(data);
      } else {
        modal?.close();
      }
    } catch (e) {
      modal?.close();
    } finally {
      setIsUrlFetching(false);
    }
  }, [nodeId, isCurrentlyActiveNode, startDownloadingFile, modal]);

  const sendReport = useCallback(
    (time: number, currentPage: number) => {
      try {
        api.dataRoom.reports.trackTime(
          dataRoomId,
          nodeId,
          currentPage,
          time,
          version || 1,
        );
      } catch (e) {
        return;
      }
    },
    [dataRoomId, nodeId, version],
  );

  const { onDocumentViewed } = useMixpanelDocumentEvents({
    documentName,
    dataRoomName,
  });

  const { isLoaded, onDocumentLoad, onPageChange } = useFilePreviewTrackTime({
    isModalOpen,
    sendReport,
    onDocumentViewed,
  });

  const onSoftSignalSuccess = useCallback(
    (lastSoftSignal: SoftSignal, withdrawInterest?: boolean) => {
      setData((prev) => ({ ...prev, lastSoftSignal }));

      if (withdrawInterest) {
        popSuccess(t('interestWithdrawnSuccessfully'));
        modal?.close();
        setTimeout(() => navigate(BUYING_PORTFOLIO_ROUTE), 2000);
      }
    },
    [modal, navigate, popSuccess, t],
  );

  const RenderFooter = useMemo(
    () =>
      isLoaded &&
      displayBanner && (
        <SoftSignals
          lastSoftSignal={lastSoftSignal}
          dataRoomNodeId={nodeId}
          dataRoomId={dataRoomId}
          dealRoomId={dataRoom?.dealRoomId}
          onSuccess={onSoftSignalSuccess}
        />
      ),
    [
      isLoaded,
      displayBanner,
      lastSoftSignal,
      dataRoomId,
      dataRoom,
      nodeId,
      onSoftSignalSuccess,
    ],
  );

  // fetch when open, reset url on close because we want to track view count
  useEffect(() => {
    if (isModalOpen) {
      if (!data && !isUrlFetching) {
        fetchUrl();
      }
      return;
    }

    if (data) setData(null);
  }, [isUrlFetching, url, isModalOpen, fetchUrl, data]);

  useCloseOnEscape(modal?.close);

  return (
    <Modal
      ref={modalRef}
      className={classes}
      title={RenderTitle}
      footer={RenderFooter}
      lockScroll={false}
    >
      {isUrlFetching && <Loader />}

      {isEml(type) && <CannotPreviewInBrowser />}

      {isPdf(type) && (
        <PdfViewer
          url={url}
          onPageChange={onPageChange}
          onDocumentLoad={onDocumentLoad}
        />
      )}

      {isOfficeFileType(type) && (
        <OfficeFilePreview
          data={data}
          email={email}
          fullName={userFullName}
          watermarkSettings={extractNodeWatermarkSettings(node)}
          onDocumentLoad={onDocumentLoad}
        />
      )}

      {isDwg(type) &&
        (!urn || !isAutodeskProccessed ? (
          <CannotPreviewInBrowser />
        ) : (
          <AutodeskViewer
            urn={urn}
            accessToken={autodeskToken}
            email={email}
            fullName={userFullName}
            ipAddress={data?.ipAddress}
            watermarkSettings={extractNodeWatermarkSettings(node)}
            onDocumentLoad={onDocumentLoad}
          />
        ))}

      {isImage(type) && (
        <img
          alt={documentName}
          src={url}
          onLoadedData={() => onDocumentLoad()}
        />
      )}

      {isVideo(type) && (
        <video
          src={url}
          controls
          controlsList="nodownload"
          onLoadedData={() => onDocumentLoad()}
        />
      )}
    </Modal>
  );
};

export default forwardRef(DataroomNodeViewModal);
